import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import type { RootState } from 'store';
import { CustomerFormFields } from 'page/CustomerPage/types/customerFormField';
import { type EditStep, EDIT_STEP } from 'page/CustomerPage/types/editStep';
import {
  Channel,
  CHANNELS,
  EnvironmentType,
  ENVIRONMENT_TYPES,
  OriginalCustomerDetail,
} from 'types/customer';
import { OriginalZeusToken } from 'types/zeusToken';
import { convertUTCStringIntoJSTString } from 'utils/dateUtils';

/**
 * 顧客情報の編集画面の状態
 */
interface EditState {
  /**
   * ロード中フラグ
   */
  loading: boolean;
  /**
   * 編集モーダル表示フラグ
   */
  isOpen: boolean;
  /**
   * 編集のステップ
   */
  step: EditStep;
  /**
   * 顧客名
   */
  id: number | undefined;
  /**
   * 顧客名
   */
  name: string;
  /**
   * 顧客詳細
   */
  detail: string;
  /**
   * 環境（GCP プロジェクト名）
   */
  environment: string;
  /**
   * 環境種別
   * NOTE: 環境種別は当分は gcp しか使用しない
   */
  environmentType: EnvironmentType;
  /**
   * 顧客番号
   */
  corporateNumber: string;
  /**
   * 契約種別
   */
  channel: Channel;
  /**
   * 顧客環境URL
   */
  poseidonUrl: string;
  /**
   * 顧客ステータス
   */
  status: boolean;
  /**
   * システム管理上のグループ
   */
  systemUpdateGroup: string;
  /**
   * IAP Client ID
   */
  iapClientId: string;
  /**
   * IAP Client Secret
   */
  iapClientSecret: string;
  /**
   * システムの現在バージョン
   */
  currentSystemVersion: string;
  /**
   * システムの次回バージョン
   */
  targetSystemVersion: string;
  /**
   * Poseidon Agent CPU
   */
  poseidonAgentCpu: string;
  /**
   * Poseidon Agent Memory
   */
  poseidonAgentMemory: string;
  /**
   * VPA有効フラグ
   */
  enableVerticalPodAutoScaler: boolean;
  /**
   * Zeus トークン
   */
  zeusToken: string;
  /**
   * Zeus トークン作成日
   */
  zeusTokenCreatedAt: string;
  /**
   * Zeus トークンステータス
   */
  zeusTokenExpired: boolean;
  /**
   * 顧客詳細取得日時
   */
  customerDetailFetchedTime: string | undefined;
}

/**
 * 顧客情報編集画面の初期状態
 */
const initialState: EditState = {
  loading: false,
  isOpen: false,
  step: EDIT_STEP.INPUT,
  id: undefined,
  name: '',
  detail: '',
  environment: '',
  environmentType: ENVIRONMENT_TYPES.GCP,
  corporateNumber: '',
  channel: CHANNELS.STABLE,
  poseidonUrl: '',
  status: true,
  systemUpdateGroup: '',
  iapClientId: '',
  iapClientSecret: '',
  currentSystemVersion: '',
  targetSystemVersion: '',
  poseidonAgentCpu: '',
  poseidonAgentMemory: '',
  enableVerticalPodAutoScaler: false,
  zeusToken: '',
  zeusTokenCreatedAt: '',
  zeusTokenExpired: true,
  customerDetailFetchedTime: undefined,
};

/**
 * 顧客情報編集画面の slice(reducer + action)
 */
export const editSlice = createSlice({
  name: 'edit',
  initialState,
  reducers: {
    init: () => initialState,
    loadStarted: (state) => {
      state.loading = true;
    },
    loadEnded: (state) => {
      state.loading = false;
    },
    modalOpened: (state) => {
      state.isOpen = true;
    },
    stepMovedToConfirmation: (state) => {
      state.step = EDIT_STEP.CONFIRMATION;
    },
    stepMovedToInput: (state) => {
      state.step = EDIT_STEP.INPUT;
    },
    stepMovedToDelete: (state) => {
      state.step = EDIT_STEP.DELETE;
    },
    formGroupUpdated: (
      state,
      action: PayloadAction<{
        form: CustomerFormFields;
      }>,
    ) => {
      state.name = action.payload.form.name;
      state.detail = action.payload.form.detail;
      state.environment = action.payload.form.environment;
      state.environmentType = action.payload.form.environmentType;
      state.corporateNumber = action.payload.form.corporateNumber;
      state.channel = action.payload.form.channel;
      state.poseidonUrl = action.payload.form.poseidonUrl;
      state.systemUpdateGroup = action.payload.form.systemUpdateGroup;
      state.iapClientId = action.payload.form.iapClientId;
      state.iapClientSecret = action.payload.form.iapClientSecret;
      state.currentSystemVersion = action.payload.form.currentSystemVersion;
      state.targetSystemVersion = action.payload.form.targetSystemVersion;
      state.poseidonAgentCpu = action.payload.form.poseidonAgentCpu;
      state.poseidonAgentMemory = action.payload.form.poseidonAgentMemory;
      state.enableVerticalPodAutoScaler =
        action.payload.form.enableVerticalPodAutoScaler;
      state.status = action.payload.form.status;
    },
    fetchCustomerSuccess: (
      state,
      action: PayloadAction<{
        id: number;
        status: boolean;
        originalCustomerDetail: OriginalCustomerDetail;
        originalZeusToken: OriginalZeusToken;
        customerDetailFetchedTime: string;
      }>,
    ) => {
      state.id = action.payload.id;
      state.name = action.payload.originalCustomerDetail.name;
      state.detail = action.payload.originalCustomerDetail.detail;
      state.environment = action.payload.originalCustomerDetail.environment;
      state.environmentType =
        action.payload.originalCustomerDetail.environmentType;
      state.corporateNumber =
        action.payload.originalCustomerDetail.corporateNumber;
      state.channel = action.payload.originalCustomerDetail.channel;
      state.poseidonUrl = action.payload.originalCustomerDetail.poseidonUrl;
      state.systemUpdateGroup =
        action.payload.originalCustomerDetail.systemUpdateGroup;
      state.iapClientId = action.payload.originalCustomerDetail.iapClientId;
      state.iapClientSecret =
        action.payload.originalCustomerDetail.iapClientSecret;
      state.currentSystemVersion =
        action.payload.originalCustomerDetail.currentSystemVersion;
      state.targetSystemVersion =
        action.payload.originalCustomerDetail.targetSystemVersion;
      state.poseidonAgentCpu =
        action.payload.originalCustomerDetail.poseidonAgentCpu || '';
      state.poseidonAgentMemory =
        action.payload.originalCustomerDetail.poseidonAgentMemory || '';
      state.enableVerticalPodAutoScaler =
        action.payload.originalCustomerDetail.enableVerticalPodAutoScaler ||
        false;
      state.zeusToken = action.payload.originalZeusToken.token;
      state.zeusTokenCreatedAt = action.payload.originalZeusToken.createdAt;
      state.zeusTokenExpired = action.payload.originalZeusToken.expired;
      state.customerDetailFetchedTime =
        action.payload.customerDetailFetchedTime;
      state.status = action.payload.status;
    },
  },
});

/**
 * 顧客情報編集画面の action
 */
export const editActions = editSlice.actions;

/**
 * 顧客情報登録画面の selectors
 */
export const editSelectors = {
  loading: (state: RootState) => state.customer.edit.loading,
  isOpenInputModal: (state: RootState) =>
    state.customer.edit.isOpen && state.customer.edit.step === EDIT_STEP.INPUT,
  isOpenConfirmationModal: (state: RootState) =>
    state.customer.edit.isOpen &&
    state.customer.edit.step === EDIT_STEP.CONFIRMATION,
  isOpenDeleteModal: (state: RootState) =>
    state.customer.edit.isOpen && state.customer.edit.step === EDIT_STEP.DELETE,
  id: (state: RootState) => state.customer.edit.id,
  name: (state: RootState) => state.customer.edit.name,
  detail: (state: RootState) => state.customer.edit.detail,
  environment: (state: RootState) => state.customer.edit.environment,
  environmentType: (state: RootState) => state.customer.edit.environmentType,
  corporateNumber: (state: RootState) => state.customer.edit.corporateNumber,
  channel: (state: RootState) => state.customer.edit.channel,
  poseidonUrl: (state: RootState) => state.customer.edit.poseidonUrl,
  status: (state: RootState) => state.customer.edit.status,
  systemUpdateGroup: (state: RootState) =>
    state.customer.edit.systemUpdateGroup,
  iapClientId: (state: RootState) => state.customer.edit.iapClientId,
  iapClientSecret: (state: RootState) => state.customer.edit.iapClientSecret,
  currentSystemVersion: (state: RootState) =>
    state.customer.edit.currentSystemVersion,
  targetSystemVersion: (state: RootState) =>
    state.customer.edit.targetSystemVersion,
  poseidonAgentCpu: (state: RootState) => state.customer.edit.poseidonAgentCpu,
  poseidonAgentMemory: (state: RootState) =>
    state.customer.edit.poseidonAgentMemory,
  enableVerticalPodAutoScaler: (state: RootState) =>
    state.customer.edit.enableVerticalPodAutoScaler,
  zeusToken: (state: RootState) => state.customer.edit.zeusToken,
  zeusTokenCreatedAt: (state: RootState) => {
    const createdAt = state.customer.edit.zeusTokenCreatedAt;

    return createdAt ? convertUTCStringIntoJSTString(createdAt) : '';
  },
  zeusTokenExpired: (state: RootState) =>
    state.customer.edit.zeusTokenExpired ? '無効' : '有効',
  customerDetailFetchedTime: (state: RootState) =>
    state.customer.edit.customerDetailFetchedTime,
} as const;
